import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faImage } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { customSwal } from '../CustomSwal';
import MarkdownEditor from './MarkdownEditor';
import { ACCEPTED_IMAGE_TYPES, uploadConsoleImage } from './consoleMediaApi';

// Each list item shows either an image or a link/video. `mediaType` only lives in the draft;
// saved items are told apart by which of `image` / `link` is filled in.
const getMediaType = (item) => item.mediaType || (item.link ? 'link' : 'image');

export const validateListItems = (items) => {
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item.title?.trim() || !item.description?.trim()) {
      return `List item ${i + 1} needs a title and description.`;
    }
    if (getMediaType(item) === 'link' && !item.link?.trim()) {
      return `List item ${i + 1} needs a link.`;
    }
  }
  return null;
};

export const toSavedListItems = (items) => items.map(({ mediaType, ...item }) => item);

const ListItemForm = ({ listItems, setListItems, consoleId }) => {
  const token = useSelector((state) => state.userLogin.userInfo?.token);
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const updateListItem = (index, field, value) => {
    setListItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  // Switching media clears the other field, same as the single list item modal
  const setMediaType = (index, mediaType) => {
    setListItems((prev) => prev.map((item, i) => (
      i === index
        ? {
            ...item,
            mediaType,
            image: mediaType === 'link' ? '' : item.image,
            link: mediaType === 'image' ? '' : item.link,
          }
        : item
    )));
  };

  // Upload right away so the item stores a hosted URL instead of a base64 blob
  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setUploadingIndex(index);
    try {
      const { url } = await uploadConsoleImage(file, { consoleId }, token);
      updateListItem(index, 'image', url);
    } catch (error) {
      customSwal.fire({
        icon: 'error',
        title: 'Image upload failed',
        text: error.message,
      });
    } finally {
      setUploadingIndex(null);
    }
  };

  const addNewListItem = () => {
    const newItem = {
      title: '',
      description: '',
      image: '',
      link: '',
      mediaType: 'image',
    };
    setListItems([...listItems, newItem]);
  };

  const removeListItem = (index) => {
    const updated = listItems.filter((_, i) => i !== index);
    setListItems(updated);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="block font-medium">List Items:</label>
        <button
          type="button"
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-sm"
          onClick={addNewListItem}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          Add Item
        </button>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {listItems.map((listItem, index) => {
          const mediaType = getMediaType(listItem);

          return (
            <div key={index} className="bg-gray-700 p-3 rounded">
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm font-medium">Item {index + 1}</span>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs"
                  onClick={() => removeListItem(index)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium mb-1">Title:</label>
                  <input
                    type="text"
                    className="w-full p-2 rounded bg-gray-600 text-white text-sm"
                    value={listItem.title}
                    onChange={(e) => updateListItem(index, 'title', e.target.value)}
                    placeholder="Enter title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Description (Markdown supported):</label>
                  <MarkdownEditor
                    value={listItem.description}
                    onChange={(value) => updateListItem(index, 'description', value)}
                    height={150}
                    uploadTarget={{ consoleId }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Content Type:</label>
                  <div className="flex space-x-2 mb-2">
                    <button
                      type="button"
                      className={`px-2 py-1 rounded text-xs flex items-center ${
                        mediaType === 'image'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                      onClick={() => setMediaType(index, 'image')}
                    >
                      <FontAwesomeIcon icon={faImage} className="mr-1" />
                      Image
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-1 rounded text-xs flex items-center ${
                        mediaType === 'link'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                      onClick={() => setMediaType(index, 'link')}
                    >
                      <FontAwesomeIcon icon={faYoutube} className="mr-1" />
                      Link/Video
                    </button>
                  </div>

                  {mediaType === 'image' ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        onChange={(e) => handleImageUpload(e, index)}
                        className="hidden"
                        id={`image-upload-${index}`}
                        disabled={uploadingIndex !== null}
                      />
                      <label
                        htmlFor={`image-upload-${index}`}
                        className={`bg-blue-500 text-white font-bold py-1 px-2 rounded text-xs ${
                          uploadingIndex !== null ? 'opacity-50 cursor-wait' : 'hover:bg-blue-700 cursor-pointer'
                        }`}
                      >
                        <FontAwesomeIcon icon={faImage} className="mr-1" />
                        {uploadingIndex === index ? 'Uploading…' : 'Upload Image'}
                      </label>
                      {listItem.image && (
                        <div className="flex items-center space-x-2">
                          <img
                            src={listItem.image}
                            alt="Preview"
                            className="w-8 h-8 object-cover rounded"
                          />
                          <button
                            type="button"
                            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-1 rounded text-xs"
                            onClick={() => updateListItem(index, 'image', '')}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <input
                      type="url"
                      className="w-full p-2 rounded bg-gray-600 text-white text-sm"
                      value={listItem.link || ''}
                      onChange={(e) => updateListItem(index, 'link', e.target.value)}
                      placeholder="Enter YouTube URL or other link"
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {listItems.length === 0 && (
          <p className="text-gray-400 italic text-center py-4">
            No list items yet. Click "Add Item" to get started!
          </p>
        )}
      </div>
    </div>
  );
};

export default ListItemForm;
